import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Lock, Crown, ArrowUpRight, Phone, MessageCircle, IndianRupee, ScanLine } from "lucide-react";
import { api, getUser, API } from "../lib/api";
import { AppShell } from "./AppShell";
import { RechargeBox } from "./RechargeCredits";

// Keep this matrix in sync with backend/server.py:`TIER_NAV_ACCESS` and
// frontend/src/components/AppShell.jsx:`TIER_NAV_ACCESS`.
const TIER_NAV_ACCESS = {
  starter:  new Set(["auto_listing", "profile", "billing"]),
  pro:      new Set(["auto_listing", "upload", "dashboard", "analytics", "insights", "growth", "reports", "profile", "billing"]),
  business: new Set(["auto_listing", "upload", "dashboard", "analytics", "insights", "growth", "reports", "catalogue", "profile", "billing"]),
};

/**
 * Route-level tier gate.
 * Wrap a protected seller route with <TierGate tierKey="catalogue">…</TierGate>
 * If the logged-in seller's plan_tier doesn't include `tierKey`, we render an
 * Upgrade required screen instead of the original page (so the page never
 * mounts and gated APIs are never called).
 *
 * Why this matters: the sidebar already shows a lock badge, but a user could
 * still navigate directly via URL bar / back button. Without this guard,
 * Starter users could read Pro/Business pages. We fetch /auth/me once so the
 * cached `bb_user` localStorage object (which may be stale) is replaced with
 * the server's current tier before deciding.
 */
export default function TierGate({ tierKey, children }) {
  const [user, setUser] = useState(getUser());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    api.get("/auth/me")
      .then(({ data }) => {
        if (!on) return;
        localStorage.setItem("bb_user", JSON.stringify(data));
        setUser(data);
      })
      .catch(() => {})
      .finally(() => on && setLoading(false));
    return () => { on = false; };
  }, []);

  // Admin / anything not a seller skips the gate entirely.
  if (user && user.role !== "seller") return children;

  // Until we have a confirmed tier from the server, show a lightweight
  // skeleton so we don't flash the gated content while /auth/me is in flight.
  if (loading && !user?.plan_tier) {
    return (
      <AppShell role="seller">
        <div className="min-h-[60vh] grid place-items-center" data-testid="tier-gate-loading">
          <div className="w-10 h-10 rounded-full border-2 border-[#FF3F6C]/30 border-t-[#FF3F6C] animate-spin" />
        </div>
      </AppShell>
    );
  }

  const tier = (user?.plan_tier && TIER_NAV_ACCESS[user.plan_tier]) ? user.plan_tier : "business";
  // Trial accounts get ONLY the Listings tab; everything else is locked
  // behind the ₹999/month unlock (100 listings + Data Analysis).
  const isTrialUser = user?.account_type === "trial";
  const allowed = isTrialUser
    ? new Set(["auto_listing", "profile", "billing"])
    : TIER_NAV_ACCESS[tier];

  // Hard credit lock — two triggers:
  //   1. `account_locked=true` on the user record (admin set exact quota and
  //      the seller consumed it; backend flips this flag). Authoritative.
  //   2. Fallback for legacy accounts: listings_credits<=0 AND used>0.
  // Either way we lock EVERY tab and show the recharge screen so the
  // seller can pay & resume (or contact admin for a new quota).
  const creditsRemaining = Number(user?.listings_credits || 0);
  const creditsUsed = Number(user?.listings_credits_used || 0);
  const explicitLock = !!user?.account_locked;
  const isPaidDrained = !!user && user.account_type === "full" && (
    explicitLock || (creditsRemaining <= 0 && creditsUsed > 0)
  );
  if (isPaidDrained) {
    return (
      <AppShell role="seller">
        <CreditsExhaustedScreen
          email={user.email}
          usedTotal={creditsUsed}
          reason={user.account_locked_reason || "credits_drained"}
        />
      </AppShell>
    );
  }

  if (!tierKey || allowed.has(tierKey)) return children;
  if (!user) return <Navigate to="/login" replace />;

  // Locked — render upgrade-required screen INSIDE the seller's AppShell
  // so the sidebar (with lock badges on the other tabs) stays visible and
  // the seller can still navigate to Auto Listings or Profile.
  const TIER_LABEL = { starter: "Starter", pro: "Pro", business: "Business" };
  const requiredTier = "business";

  // Trial users get the ₹999/month self-serve unlock (100 listings +
  // Data Analysis) instead of the generic plans screen.
  if (isTrialUser) {
    return (
      <AppShell role="seller">
        <TrialUnlockScreen />
      </AppShell>
    );
  }
  return (
    <AppShell role="seller">
      <div className="min-h-[70vh] grid place-items-center p-6" data-testid="tier-gate-upgrade">
        <div
          className="max-w-xl w-full rounded-3xl p-8 sm:p-10 text-center"
          style={{
            background: "#FFFFFF",
            border: "1px solid #EDEDF1",
            boxShadow: "0 40px 100px -30px rgba(124,58,237,0.35), 0 8px 24px -12px rgba(15,23,42,0.08)",
          }}
        >
          <div
            className="w-16 h-16 rounded-2xl grid place-items-center mx-auto"
            style={{
              background: "linear-gradient(135deg, #FFF1F5 0%, #F5EFFF 100%)",
              color: "#FF3F6C",
              border: "1px solid #FFD3DD",
            }}
          >
            <Lock size={28} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold mt-5" style={{ color: "#FF3F6C" }}>
            Upgrade required · You are on {TIER_LABEL[tier] || "Trial"}
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold mt-2" style={{ fontFamily: "'Outfit', sans-serif", color: "#0B0C12" }}>
            This tab is part of the <span style={{ color: "#7C3AED" }}>{TIER_LABEL[requiredTier]}</span> plan.
          </h2>
          <p className="text-sm mt-3 leading-relaxed" style={{ color: "#535665" }}>
            Your current plan unlocks core listings only. Upgrade to access
            analytics, ads suggestions, My Catalogue and every other feature.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/pricing#pricing"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold"
              style={{ background: "linear-gradient(135deg, #FF3F6C 0%, #FF905A 100%)", color: "#FFFFFF", boxShadow: "0 14px 30px -10px rgba(255,63,108,0.55)" }}
              data-testid="tier-gate-upgrade-cta"
            >
              <Crown size={14} /> See plans <ArrowUpRight size={14} />
            </Link>
            <a
              href="https://wa.me/919464645491"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold"
              style={{ background: "#25D366", color: "#FFFFFF" }}
              data-testid="tier-gate-whatsapp-cta"
            >
              <MessageCircle size={14} /> WhatsApp +91 94646 45491
            </a>
          </div>
          <a
            href="tel:+919464645491"
            className="block text-xs font-semibold mt-5"
            style={{ color: "#535665" }}
            data-testid="tier-gate-call-cta"
          >
            <Phone size={11} className="inline mr-1" /> Or call us directly
          </a>
        </div>
      </div>
    </AppShell>
  );
}

export { TIER_NAV_ACCESS };

/* ───────── Trial ₹999/month unlock screen ─────────
 * Shown when a trial seller opens any locked (data-analysis) tab.
 * One Razorpay payment of ₹999 → 100 listings + Pro tier (analytics,
 * reports, ads suggestions) for 30 days. Activation is instant.
 */
function TrialUnlockScreen() {
  const [paying, setPaying] = useState(false);
  const pay = async () => {
    setPaying(true);
    try {
      const { payWithRazorpay } = await import("../lib/razorpay");
      await payWithRazorpay({ plan_id: "monthly_999", billing_cycle: "monthly" });
      window.location.reload();
    } catch (e) {
      // Seller closed the modal or payment failed — stay on screen.
    } finally {
      setPaying(false);
    }
  };
  const payAuto = async () => {
    setPaying(true);
    try {
      const { enableAutoPay } = await import("../lib/razorpay");
      await enableAutoPay();
      window.location.reload();
    } catch (e) {
      // Cancelled or failed — stay on screen.
    } finally {
      setPaying(false);
    }
  };
  return (
    <div className="min-h-[70vh] grid place-items-center p-6" data-testid="trial-unlock-screen">
      <div
        className="max-w-xl w-full rounded-3xl p-8 sm:p-10 text-center"
        style={{
          background: "#FFFFFF",
          border: "1px solid #EDEDF1",
          boxShadow: "0 40px 100px -30px rgba(124,58,237,0.35), 0 8px 24px -12px rgba(15,23,42,0.08)",
        }}
      >
        <div
          className="w-16 h-16 rounded-2xl grid place-items-center mx-auto"
          style={{
            background: "linear-gradient(135deg, #FFF1F5 0%, #F5EFFF 100%)",
            color: "#FF3F6C",
            border: "1px solid #FFD3DD",
          }}
        >
          <Lock size={28} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold mt-5" style={{ color: "#FF3F6C" }}>
          Locked on Trial
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold mt-2" style={{ fontFamily: "'Outfit', sans-serif", color: "#0B0C12" }}>
          Unlock everything for <span style={{ color: "#7C3AED" }}>₹999/month</span>
        </h2>
        <p className="text-sm mt-3 leading-relaxed" style={{ color: "#535665" }}>
          Your trial includes AI Auto Listings only. The monthly plan gives you{" "}
          <b style={{ color: "#0B0C12" }}>100 AI listings + full Data Analysis</b>{" "}
          (Dashboard, Growth Analytics, Ads Suggestions, Reports) — activated
          instantly after payment.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            onClick={pay}
            disabled={paying}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #FF3F6C 0%, #FF905A 100%)", color: "#FFFFFF", boxShadow: "0 14px 30px -10px rgba(255,63,108,0.55)" }}
            data-testid="trial-unlock-pay-btn"
          >
            <IndianRupee size={14} /> {paying ? "Opening Razorpay…" : "Pay ₹999 & Unlock Now"}
          </button>
          <button
            onClick={payAuto}
            disabled={paying}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold disabled:opacity-60"
            style={{ background: "#7C3AED", color: "#FFFFFF" }}
            data-testid="trial-unlock-autopay-btn"
            title="Authorize once — ₹999 deducts automatically every month"
          >
            Enable Auto-Pay ₹999/mo
          </button>
          <a
            href="https://wa.me/919464645491"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold"
            style={{ background: "#25D366", color: "#FFFFFF" }}
            data-testid="trial-unlock-whatsapp"
          >
            <MessageCircle size={14} /> WhatsApp us
          </a>
        </div>
        <div className="text-[11px] mt-4" style={{ color: "#94969F" }}>
          UPI QR / card / netbanking via Razorpay · GST invoice included
        </div>
      </div>
    </div>
  );
}

/* ───────── Credits Exhausted full-screen lock ─────────
 * Shown for paid sellers who have drained their balance. Renders the 3-plan
 * QR codes inline so the seller can pick a plan and pay without leaving
 * the workspace. Once admin verifies payment & tops them up, the next
 * `/auth/me` refresh removes the lock automatically.
 */
function CreditsExhaustedScreen({ email, usedTotal, reason }) {
  const isAdminQuotaLock = reason === "quota_exhausted";

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center p-6 sm:p-10" data-testid="credits-exhausted-lock">
      <div className="max-w-4xl w-full">
        <div className="text-center">
          <div
            className="w-16 h-16 rounded-2xl grid place-items-center mx-auto"
            style={{
              background: "linear-gradient(135deg, #FFF1F5 0%, #FEE2E2 100%)",
              color: "#DC2626",
              border: "1px solid #FCA5A5",
            }}
          >
            <Lock size={28} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold mt-5" style={{ color: "#DC2626" }}>
            {isAdminQuotaLock ? "Quota exhausted · Contact admin to renew" : "Account locked · 0 listings remaining"}
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold mt-2" style={{ fontFamily: "'Outfit', sans-serif", color: "#0B0C12" }}>
            {isAdminQuotaLock
              ? "Your listing quota has been used up"
              : "Recharge to continue using EcomCatalog AI"}
          </h2>
          <p className="text-sm mt-3 leading-relaxed max-w-2xl mx-auto" style={{ color: "#535665" }}>
            You&apos;ve used all your listing credits ({usedTotal} total). Buy
            credits below — ₹10 = 1 AI Auto Listing. Pay any amount via
            Razorpay (UPI QR / card) and your account unlocks instantly.
            Login email <span className="font-mono text-[#0B0C12]">{email}</span>.
          </p>
        </div>

        {/* Self-serve instant recharge — ₹10 per listing, Razorpay UPI QR/card,
            credits auto-activate the moment payment succeeds (no admin). */}
        <div
          className="mt-8 rounded-2xl p-5 border-2 bg-white"
          style={{ borderColor: "#03A685", boxShadow: "0 24px 60px -22px rgba(3,166,133,0.35)" }}
          data-testid="instant-recharge-card"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold bg-[#03A685] text-white">
              Instant · No waiting
            </span>
          </div>
          <div className="text-lg font-bold" style={{ color: "#0B0C12" }}>
            Pay any amount — listings activate instantly
          </div>
          <div className="text-xs mb-4" style={{ color: "#535665" }}>
            ₹10 = 1 AI Auto Listing. Pay via UPI QR / card and your account unlocks automatically within seconds.
          </div>
          <RechargeBox onSuccess={() => window.location.reload()} />
        </div>

        {/* Help row */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a
            href="https://wa.me/919464645491"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold text-white"
            style={{ background: "#25D366" }}
            data-testid="recharge-whatsapp-cta"
          >
            <MessageCircle size={13} /> WhatsApp +91 94646 45491
          </a>
          <a
            href="tel:+919464645491"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold"
            style={{ background: "#F3F4F6", color: "#0B0C12", border: "1px solid #E5E7EB" }}
            data-testid="recharge-call-cta"
          >
            <Phone size={12} /> Or call +91 94646 45491
          </a>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold"
            style={{ background: "#F3F4F6", color: "#0B0C12", border: "1px solid #E5E7EB" }}
            data-testid="recharge-pricing-link"
          >
            <ArrowUpRight size={12} /> See full plan details
          </Link>
        </div>
        <p className="text-[11px] mt-4 text-center" style={{ color: "#94969F" }}>
          Pricing is exclusive of GST — no GST added. Payments are non-refundable.
        </p>
      </div>
    </div>
  );
}
